/**
 * WeeklyReview
 * ──────────────────────────────────────────────────────────────────────
 * Manager-facing weekly digest of overbooking AI recommendations.
 *
 * Demo mode: renders the `providers` + `narrative` fixture passed in.
 * Real mode: caller wires this to `/api/scheduler/weekly-review` via
 *            `schedulerApi.getWeeklyReview` and `applyWeeklyReview`.
 *
 * Props:
 *   weekStart      string   — ISO date of Monday of the reviewed week.
 *   narrative      string   — LLM-generated summary paragraph.
 *   providers      array    — [{ id, name, current_limit, ai_suggested,
 *                                show_rate, total_appointments, no_shows,
 *                                trend: 'higher'|'lower'|'match', rationale }]
 *   loading        bool
 *   error          object|null
 *   onRefresh      fn
 *   onApplyAll     fn       — applies every non-matching suggestion
 *   onApplyOne     fn(id)   — applies a single provider's AI suggestion
 *   onKeepOne      fn(id)   — explicitly keeps the current setting
 *   readOnly       bool     — disables Apply/Keep buttons when true.
 */
import { Card, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { cn } from '../../../../lib/utils';
import {
  Sparkles, TrendingUp, TrendingDown, CheckCircle2, RefreshCw, Loader2,
  AlertTriangle,
} from 'lucide-react';

const TREND_META = {
  higher: { icon: TrendingUp, cls: 'text-emerald-600', label: 'Raise' },
  lower: { icon: TrendingDown, cls: 'text-amber-600', label: 'Lower' },
  match: { icon: CheckCircle2, cls: 'text-slate-500', label: 'On target' },
};

const formatWeekLabel = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (_e) {
    return iso;
  }
};

const WeeklyReview = ({
  weekStart,
  narrative,
  providers = [],
  loading = false,
  error = null,
  onRefresh,
  onApplyAll,
  onApplyOne,
  onKeepOne,
  readOnly = false,
}) => {
  const actionable = providers.filter((p) => p.trend !== 'match');
  return (
    <div className="space-y-4" data-testid="weekly-review">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-500" />
            <h2 className="text-lg font-semibold text-slate-900">Weekly Scheduler Review</h2>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Week of {formatWeekLabel(weekStart)} · {providers.length} providers reviewed
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1"
              onClick={onRefresh}
              disabled={loading}
              data-testid="weekly-review-refresh"
            >
              {loading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Refresh
            </Button>
          )}
          <Button
            size="sm"
            className="h-8 text-xs bg-[#6b5ce7] hover:bg-[#5a4bd6] gap-1"
            onClick={onApplyAll}
            disabled={readOnly || loading || actionable.length === 0}
            data-testid="weekly-review-apply-all"
          >
            <Sparkles className="h-3 w-3" />
            Apply all ({actionable.length})
          </Button>
        </div>
      </div>

      {/* Narrative card */}
      <Card className="border-violet-200 bg-violet-50/40" data-testid="weekly-review-narrative-card">
        <CardContent className="p-4">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating narrative…
            </div>
          )}
          {!loading && error && (
            <div className="flex items-center gap-2 text-xs text-red-700" data-testid="weekly-review-error">
              <AlertTriangle className="h-3.5 w-3.5" />
              {error?.message || 'Failed to load the weekly review.'}
            </div>
          )}
          {!loading && !error && (
            <p
              className="text-sm text-slate-700 leading-relaxed"
              data-testid="weekly-review-narrative"
            >
              {narrative || 'No narrative available for this week.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Per-provider cards */}
      <div className="space-y-2">
        {!loading && !error && providers.length === 0 && (
          <div
            className="p-6 rounded-lg border border-dashed border-slate-200 text-center text-[11px] text-slate-500"
            data-testid="weekly-review-empty"
          >
            No providers configured yet — add providers in Settings to start the weekly review.
          </div>
        )}
        {providers.map((p) => {
          const meta = TREND_META[p.trend] || TREND_META.match;
          const Icon = meta.icon;
          const showActions = p.trend !== 'match';
          return (
            <Card
              key={p.id}
              className="border-slate-200"
              data-testid={`weekly-review-provider-${p.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={cn('mt-0.5', meta.cls)}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">{p.name}</span>
                      <Badge
                        className={cn(
                          'text-[9px] border-0',
                          p.trend === 'higher'
                            ? 'bg-emerald-50 text-emerald-700'
                            : p.trend === 'lower'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                        )}
                      >
                        {meta.label}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-slate-600 mt-1">{p.rationale}</p>
                    <div className="grid grid-cols-4 gap-2 text-[10px] mt-2 pt-2 border-t border-slate-100">
                      <div>
                        <span className="text-slate-400">Show rate</span>
                        <p className="text-slate-800 font-medium">{p.show_rate}%</p>
                      </div>
                      <div>
                        <span className="text-slate-400">30-day appts</span>
                        <p className="text-slate-800 font-medium">{p.total_appointments}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Current limit</span>
                        <p className="text-slate-800 font-medium">{p.current_limit}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">AI suggests</span>
                        <p
                          className={cn(
                            'font-medium',
                            p.trend === 'higher'
                              ? 'text-emerald-700'
                              : p.trend === 'lower'
                              ? 'text-amber-700'
                              : 'text-slate-800'
                          )}
                          data-testid={`weekly-review-suggested-${p.id}`}
                        >
                          {p.ai_suggested}
                        </p>
                      </div>
                    </div>
                  </div>
                  {showActions && (
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        className="h-7 text-[10px] bg-[#6b5ce7] hover:bg-[#5a4bd6]"
                        onClick={() => onApplyOne?.(p)}
                        disabled={readOnly || loading}
                        data-testid={`weekly-review-apply-${p.id}`}
                      >
                        Apply
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[10px]"
                        onClick={() => onKeepOne?.(p)}
                        disabled={readOnly || loading}
                        data-testid={`weekly-review-keep-${p.id}`}
                      >
                        Keep
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyReview;
